import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Globe, BriefcaseBusiness, CalendarCheck, Info, CheckCircle2, ChevronLeft, ClipboardCheck, Clock3, DoorOpen, FileCheck2, FileText, GraduationCap, IdCard, Landmark, Minus, PlaneTakeoff, Plus, ShieldCheck, Stethoscope, Headphones, UserRound, Send } from "lucide-react";
import { COUNTRIES, VISA_TYPES } from "../Visa";
import czechImage from "../../../../assets/Places/london.png";
import turkeyImage from "../../../../assets/holidays/thailand.png";
import hungaryImage from "../../../../assets/Places/paris.png";
import icelandImage from "../../../../assets/hero-top/activities-1.png";
import consultationImage from "../../../../assets/hero-top/visa.png";
import documentImage from "../../../../assets/hero-top/form.png";
import expressImage from "../../../../assets/hero-top/flight.png";
import "./Type.css";
import VisaEnquiryForm from "../Form/VisaEnquiryForm";

const heroImages = {
  CZ: czechImage,
  TR: turkeyImage,
  HU: hungaryImage,
  IS: icelandImage,
};

const visaMeta = {
  Tourist: { icon: UserRound, text: "Tourist Visa" },
  Business: { icon: BriefcaseBusiness, text: "Business Visa" },
  Student: { icon: GraduationCap, text: "Student Visa" },
  Medical: { icon: Stethoscope, text: "Medical Visa" },
  Transit: { icon: PlaneTakeoff, text: "Transit Visa" },
  Work: { icon: IdCard, text: "Work Visa" },
};

const assistanceServices = [
  { label: "Consultation Services", image: consultationImage },
  { label: "Document Preparation", image: documentImage },
  { label: "Express Services", image: expressImage },
];

const applicationSteps = [
  {
    step: "STEP 1",
    title: "Choose Your Visa Type",
    text: {
      intro: "Start by selecting the visa category that matches your travel purpose.",
      points: [
        "Tourist Visa",
        "Business Visa",
        "Student Visa",
        "Medical Visa",
        "Transit Visa",
        "Work Visa",
      ],
    },
    icon: FileText,
  },
  {
    step: "STEP 2",
    title: "Share Your Travel Details",
    text: {
      intro: "Provide the basic details needed to understand your travel plans.",
      points: [
        "Destination country",
        "Number of travellers",
        "Expected travel date",
        "Purpose of travel",
      ],
    },
    icon: PlaneTakeoff,
  },
  {
    step: "STEP 3",
    title: "Submit Your Enquiry",
    text: {
      intro: "Share your contact details and any questions with our visa assistance team.",
      points: [
        "Full name",
        "Email address",
        "Phone number",
        "Additional message or requirements",
      ],
    },
    icon: Send,
  },
  {
    step: "STEP 4",
    title: "Requirement Review",
    text: {
      intro: "Our team reviews your enquiry and identifies the relevant requirements.",
      points: [
        "Required documents",
        "Visa-specific requirements",
        "Eligibility information",
        "Additional supporting documents",
      ],
    },
    icon: FileCheck2,
  },
  {
    step: "STEP 5",
    title: "Get Expert Assistance",
    text: {
      intro: "Our visa team will contact you with guidance based on your enquiry.",
      points: [
        "Relevant requirements",
        "Document guidance",
        "Application next steps",
        "Further assistance when required",
      ],
    },
    icon: Headphones,
  },
];
const appointmentCentreSteps = [
  {
    step: "1",
    label: "Share Your Travel Details",
    icon: FileText,
  },
  {
    step: "2",
    label: "Submit Your Enquiry",
    icon: Send,
  },
  {
    step: "3",
    label: "Requirement Review",
    icon: ShieldCheck,
  },
  {
    step: "4",
    label: "Get Expert Guidance",
    icon: Headphones,
  },
  {
    step: "5",
    label: "Prepare for Your Application",
    icon: CheckCircle2,
  },
];

export default function VisaTypePage() {
  const navigate = useNavigate();
  const { countryCode = "" } = useParams();
  const [selectedType, setSelectedType] = useState("Tourist");
  const [travellers, setTravellers] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [travelDate, setTravelDate] = useState("");

  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: "ease-out-cubic", offset: 50 });
    const timer = setTimeout(() => AOS.refresh(), 80);
    return () => clearTimeout(timer);
  }, []);

  const country = useMemo(() => (
    COUNTRIES.find(item => item.code.toLowerCase() === countryCode.toLowerCase()) || COUNTRIES[0]
  ), [countryCode]);

  const selectedMeta = visaMeta[selectedType] || visaMeta.Tourist;
const HeroIcon = selectedMeta.icon;

  return (
    <main className="visa-type-page">
      <section className="visa-type-hero">
        <img src={heroImages[country.code] || czechImage} alt={country.name} />
        <div className="visa-type-hero-shade" />
        <button type="button" className="visa-type-back" onClick={() => navigate("/visa")}>
          <ChevronLeft size={18} /> Back
        </button>
        <h1>
  <HeroIcon size={38} />
  {selectedMeta.text} for {country.name}
</h1>
      </section>

      <section className="visa-type-content">
        <div className="visa-type-main">
          <div className="visa-type-card mbt-animated-card" data-aos="fade-up">
            <h2><span className="visa-section-title-icon"><IdCard size={20} /></span> Visa Type</h2>
            <p>Kindly select your preferred visa type as per the requirement.</p>

            <div className="visa-type-options">
  {VISA_TYPES.map((type) => {
    const meta = visaMeta[type] || visaMeta.Tourist;
    const Icon = meta.icon;
    const selected = selectedType === type;

    return (
      <button
        type="button"
        key={type}
        className={`visa-type-option${selected ? " visa-type-option--active" : ""}`}
        onClick={() => setSelectedType(type)}
        aria-pressed={selected}
      >
        {selected && <CheckCircle2 className="visa-type-radio" size={16} />}

        <Icon size={34} strokeWidth={1.7} />

        <span>{meta.text}</span>

        <span className="visa-type-option-note">
          Enquiry required
        </span>
      </button>
    );
  })}
</div>
          </div>

          <div className="visa-info-strip mbt-animated-card" data-aos="fade-up" data-aos-delay="80">
  <div className="visa-info-item">
    <span className="visa-info-icon">
      <Clock3 size={25} />
    </span>
    <div>
      <strong>Visa Details</strong>
      <p>
        Validity and entry conditions depend on the selected visa category
        and current country requirements.
      </p>
    </div>
  </div>

  <div className="visa-info-item">
    <span className="visa-info-icon">
      <DoorOpen size={27} />
    </span>
    <div>
      <strong>Entry Conditions</strong>
      <p>
        Our visa team will confirm the applicable requirements after your enquiry.
      </p>
    </div>
  </div>
</div>

          <div className="visa-disclaimer">
            <span>ⓘ</span>
            <p>
  <strong>Disclaimer:</strong> Visa requirements, fees and supporting
  documents may vary by destination and visa category and may change
  without notice.
</p>
          </div>

          <div className="essential-docs-card mbt-animated-card" data-aos="fade-up">
  <h2>Documents You May Need</h2>

  <div className="essential-docs-box">
    <section>
      <h3>Passport</h3>
      <ul>
        <li>A valid passport is generally required.</li>
        <li>Passport validity and blank-page requirements vary by destination.</li>
      </ul>
    </section>

    <section>
      <h3>Travel Information</h3>
      <ul>
        <li>Expected travel date and purpose of travel.</li>
        <li>Accommodation and itinerary details may be requested.</li>
      </ul>
    </section>

    <section>
      <h3>Supporting Documents</h3>
      <ul>
        <li>Additional documents may be required depending on the visa category.</li>
        <li>Financial, employment, educational or other supporting documents may be requested where applicable.</li>
      </ul>
    </section>
  </div>
</div>

          <div className="appointment-centre-card mbt-animated-card" data-aos="fade-up">
            <h2>
  <span className="visa-section-title-icon">
    <Landmark size={20} />
  </span>
  What Happens After Your Enquiry
</h2>
            <div className="appointment-steps">
              {appointmentCentreSteps.map(item => {
                const AppointmentIcon = item.icon;
                return (
                <div className="appointment-step" key={item.step}>
                  <span className="appointment-step-number">{item.step}</span>
                  <div className="appointment-step-card">
                    <span className="appointment-step-icon"><AppointmentIcon size={20} /></span>
                    <strong>{item.label}</strong>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          <div className="essential-docs-card mbt-animated-card" data-aos="fade-up">
            <div className="essential-docs-box">
              <section>
  <h3>Travel Information</h3>

  <ul>
    <li>
      Your expected travel date will help our team understand your travel plans.
    </li>

    <li>
      Depending on the destination and visa category, additional travel
      information may be required.
    </li>
  </ul>
</section>
            </div>
          </div>

          <div className="essential-docs-card mbt-animated-card" data-aos="fade-up">
            <div className="essential-docs-box">
              <section>
  <h3>Travel Insurance</h3>

  <ul>
    <li>
      Travel insurance requirements vary by destination and visa category.
    </li>

    <li>
      Our visa team will confirm whether insurance is required for your
      selected visa.
    </li>
  </ul>
</section>
            </div>
          </div>

          <div className="essential-docs-card mbt-animated-card" data-aos="fade-up">
            <div className="essential-docs-box">
              <section>
  <h3>Financial Information</h3>

  <ul>
    <li>
      Financial documents may be required for certain visa categories.
    </li>

    <li>
      The specific documents and financial requirements depend on the
      destination and visa type.
    </li>

    <li>
      Our visa team will confirm the applicable requirements after reviewing
      your enquiry.
    </li>
  </ul>
</section>
            </div>
          </div>

          <div className="visa-processing-card">
  <div className="visa-processing-main">
    <div className="visa-processing-heading">
      <div className="visa-processing-icon">
        <Clock3 size={25} />
      </div>

      <div>
        <span className="visa-section-eyebrow">
          Processing Information
        </span>

        <h2>Visa Processing Timelines</h2>
      </div>
    </div>

    <p className="visa-processing-intro">
      Visa processing timelines vary by destination, visa category,
      applicant circumstances and the relevant authorities. The actual
      timeline can only be confirmed after reviewing the applicable
      requirements for your selected visa.
    </p>

    <div className="visa-processing-note">
      <ShieldCheck size={18} />

      <div>
        <strong>Timeline Confirmation</strong>

        <span>
          Our visa team will confirm the applicable processing information
          based on your destination, visa category and the latest available
          requirements.
        </span>
      </div>
    </div>


  </div>

  <div className="visa-processing-details">
    <div className="visa-processing-detail">
      <Clock3 size={19} />

      <div>
        <strong>Processing Time</strong>

        <span>
          The expected timeline will be confirmed by our visa team
          according to the latest applicable requirements.
        </span>
      </div>
    </div>

    <div className="visa-processing-detail">
      <FileText size={19} />

      <div>
        <strong>Visa Category</strong>

        <span>
          Processing requirements and timelines can differ depending
          on the type of visa selected for your journey.
        </span>
      </div>
    </div>

    <div className="visa-processing-detail">
      <Globe size={19} />

      <div>
        <strong>Destination</strong>

        <span>
          Each destination may follow different procedures and
          requirements set by the relevant immigration authorities.
        </span>
      </div>
    </div>
  </div>
</div>

          <div className="assistance-card mbt-animated-card" data-aos="fade-up">
            <h2><span className="visa-section-title-icon"><ShieldCheck size={20} /></span> Assistance Services</h2>
            <p>
  Get guidance on visa requirements, documents and the next steps for your
  selected destination.
</p>
            <div className="assistance-images">
              {assistanceServices.map(service => (
                <div className="assistance-image-card" key={service.label}>
                  <img src={service.image} alt={service.label} />
                  <span>{service.label}</span>
                </div>
              ))}
            </div>

            <div className="visa-assistance-cta">
  <div>
    <h3>Need Help With Your Visa?</h3>
    <p>
      Share your travel details and our visa team will contact you
      with the relevant requirements and next steps.
    </p>
  </div>

  <button
    type="button"
    onClick={() => setCalendarOpen(true)}
  >
    Get Visa Assistance
  </button>
</div>
          </div>

          <div className="application-process-card mbt-animated-card" data-aos="fade-up">
            <h2>
  <span className="visa-section-title-icon">
    <ClipboardCheck size={20} />
  </span>
  Visa Enquiry Process
</h2>
            <div className="application-process-grid">
              {applicationSteps.map((item, index) => {
                const Icon = item.icon;
                return (
                  <article
                    className={`application-step-card application-step-card--${index + 1} mbt-animated-card`}
                    key={item.step}
                    data-aos="fade-up"
                    data-aos-delay={index * 80}
                  >
                    <span className="application-step-pill">{item.step}</span>
                    <h3>{item.title}</h3>
                    <div className="application-step-text">
  <p>{item.text.intro}</p>

  <ul>
    {item.text.points.map((point) => (
      <li key={point}>{point}</li>
    ))}
  </ul>
</div>
                    <Icon className="application-step-icon" size={96} strokeWidth={1.2} />
                  </article>
                );
              })}
            </div>
          </div>
        </div>

        <aside
  className="visa-type-sidebar"
  data-aos="fade-left"
  data-aos-delay="120"
>
  <div className="visa-type-summary">

    <div className="visa-type-summary-body">

  <div className="visa-sidebar-heading">
    <span className="visa-sidebar-eyebrow">Visa Assistance</span>
    <h3>Plan Your Visa Enquiry</h3>
    <p>
      Tell us about your travel plans and our visa team will guide you
      through the relevant requirements and next steps.
    </p>
  </div>

  <div className="visa-sidebar-selection">
    <div className="visa-sidebar-selection-icon">
      <UserRound size={18} />
    </div>

    <div>
      <span>Selected Visa</span>
      <strong>{selectedMeta.text}</strong>
    </div>
  </div>

  <div className="traveller-stepper">
    <div className="traveller-stepper-label">
      <span>Add Travellers</span>
      <small>Number of applicants</small>
    </div>

    <div className="traveller-stepper-controls">
      <button
        type="button"
        onClick={() =>
          setTravellers((value) => Math.max(1, value - 1))
        }
        aria-label="Decrease travellers"
      >
        <Minus size={15} />
      </button>

      <strong>{travellers}</strong>

      <button
        type="button"
        onClick={() =>
          setTravellers((value) => Math.min(9, value + 1))
        }
        aria-label="Increase travellers"
      >
        <Plus size={15} />
      </button>
    </div>
  </div>

  <div className="visa-sidebar-info">
    <div>
      <CheckCircle2 size={16} />
      <span>Requirement guidance</span>
    </div>

    <div>
      <CheckCircle2 size={16} />
      <span>Document assistance</span>
    </div>

    <div>
      <CheckCircle2 size={16} />
      <span>Next-step support</span>
    </div>
  </div>

  <button
    type="button"
    className="visa-type-continue"
    onClick={() => setCalendarOpen(true)}
  >
    <span>
      <strong>Start Application</strong>
      <small>Begin your visa enquiry</small>
    </span>

    <ChevronLeft
      size={18}
      style={{ transform: "rotate(180deg)" }}
    />
  </button>

  <p className="visa-sidebar-note">
    No online visa application or payment is required at this stage.
  </p>

</div>
  </div>
</aside>
      </section>

      {calendarOpen && (
  <div
    className="calendar-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="calendar-title"
  >
    <div className="calendar-modal-card">
      <button
        type="button"
        className="calendar-close"
        onClick={() => {
          setCalendarOpen(false);
          setShowEnquiry(false);
        }}
        aria-label="Close visa enquiry"
      >
        ×
      </button>

      {showEnquiry ? (
        <VisaEnquiryForm
          initial={{
            country,
            visaType: selectedType,
            travellers,
            travelDate,
          }}
          onDone={() => {
            setCalendarOpen(false);
            setShowEnquiry(false);
          }}
          onCancel={() => setShowEnquiry(false)}
        />
      ) : (
        <>
          <h2 id="calendar-title">Select Travel Date</h2>

          <p>
  Select your expected travel date so our visa team can better understand
  your travel plans.
</p>

          <input
            className="calendar-input"
            type="date"
            value={travelDate}
            min={new Date().toISOString().split("T")[0]}
            onChange={(event) => setTravelDate(event.target.value)}
          />

          <button
            type="button"
            className="calendar-submit"
            disabled={!travelDate}
            onClick={() => setShowEnquiry(true)}
          >
            Continue to Enquiry
          </button>
        </>
      )}
    </div>
  </div>
)}
    </main>
  );
}
